import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Layout from '../components/layout/Layout';
import CustomerList from '../components/customers/CustomerList';
import CustomerForm from '../components/customers/CustomerForm';
import CustomerDetail from '../components/customers/CustomerDetail';
import {
  Squares2X2Icon,
  TableCellsIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  EnvelopeIcon,
  PhoneIcon,
  CalendarDaysIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  ChartBarIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';
import { SparklesIcon } from '@heroicons/react/24/solid';
import api from '../services/api';
import toast from 'react-hot-toast';

const CustomersPage = () => {
  const [currentView, setCurrentView] = useState('list'); // 'list', 'create', 'edit', 'detail'
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list', 'card'
  const [stats, setStats] = useState({ totalCustomers: 0, activeCustomers: 0, businessCustomers: 0, individualCustomers: 0 });
  const { id } = useParams();
  const [searchParams] = useSearchParams();

  // Modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingCustomer, setDeletingCustomer] = useState(null);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    // Check if we should show a specific customer detail
    const customerIdFromUrl = id || searchParams.get('id');
    if (customerIdFromUrl) {
      setSelectedCustomerId(customerIdFromUrl);
      setCurrentView('detail');
    }
    fetchStats();
  }, [id, searchParams]);

  const fetchStats = async () => {
    try {
      const response = await api.get('/customers/stats/summary');
      const data = response.data || {};
      setStats({
        totalCustomers: data.totalCustomers || 0,
        activeCustomers: data.activeCustomers || 0,
        businessCustomers: data.customerTypeStats?.business || 0,
        individualCustomers: data.customerTypeStats?.individual || 0
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleCreateCustomer = () => {
    setSelectedCustomerId(null);
    setCurrentView('create');
  };

  const handleEditCustomer = (customerId) => {
    setSelectedCustomerId(customerId);
    setCurrentView('edit');
  };

  const handleViewCustomer = (customerId) => {
    setSelectedCustomerId(customerId);
    setCurrentView('detail');
  };

  const handleBackToList = () => {
    setCurrentView('list');
    setSelectedCustomerId(null);
    // Clear URL parameters
    window.history.replaceState(null, '', '/customers');
  };

  const handleFormSuccess = () => {
    setCurrentView('list');
    setSelectedCustomerId(null);
    fetchStats(); // Refresh stats after form submission
    // Clear URL parameters
    window.history.replaceState(null, '', '/customers');
  };

  // Modal handlers
  const handleDeleteCustomer = (customer) => {
    setDeletingCustomer(customer);
    setShowDeleteModal(true);
  };

  const confirmDeleteCustomer = async () => {
    if (!deletingCustomer) return;

    try {
      await api.delete(`/customers/${deletingCustomer._id}`);
      setShowDeleteModal(false);
      setDeletingCustomer(null);
      fetchStats(); // Refresh stats after deletion
      setRefreshTrigger(prev => prev + 1); // Trigger customer list refresh
      toast.success('Customer deleted successfully');
    } catch (error) {
      console.error('Error deleting customer:', error);
      toast.error('Failed to delete customer');
    }
  };

  const handleBulkDelete = () => {
    setShowBulkDeleteModal(true);
  };

  const renderCurrentView = () => {
    switch (currentView) {
      case 'create':
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <CustomerForm
              onSuccess={handleFormSuccess}
              onCancel={handleBackToList}
            />
          </motion.div>
        );
      case 'edit':
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <CustomerForm
              customerId={selectedCustomerId}
              onSuccess={handleFormSuccess}
              onCancel={handleBackToList}
            />
          </motion.div>
        );
      case 'detail':
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <CustomerDetail
              customerId={selectedCustomerId}
              onBack={handleBackToList}
              onEdit={handleEditCustomer}
            />
          </motion.div>
        );
      default:
        return (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
            className="space-y-4 sm:space-y-6 lg:space-y-8"
          >
            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {/* Total Customers Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-4 sm:p-6 text-white shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-100 text-sm font-medium">Total Customers</p>
                    <p className="text-2xl sm:text-3xl font-bold">{stats.totalCustomers || 0}</p>
                  </div>
                  <div className="bg-green-400/20 p-2 sm:p-3 rounded-xl">
                    <UserGroupIcon className="h-6 sm:h-8 w-6 sm:w-8" />
                  </div>
                </div>
              </motion.div>

              {/* Active Customers Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-4 sm:p-6 text-white shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-emerald-100 text-sm font-medium">Active Customers</p>
                    <p className="text-2xl sm:text-3xl font-bold">{stats.activeCustomers || 0}</p>
                  </div>
                  <div className="bg-emerald-400/20 p-2 sm:p-3 rounded-xl">
                    <SparklesIcon className="h-6 sm:h-8 w-6 sm:w-8" />
                  </div>
                </div>
              </motion.div>

              {/* Business Customers Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-4 sm:p-6 text-white shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100 text-sm font-medium">Business</p>
                    <p className="text-2xl sm:text-3xl font-bold">{stats.businessCustomers || 0}</p>
                  </div>
                  <div className="bg-blue-400/20 p-2 sm:p-3 rounded-xl">
                    <BuildingOfficeIcon className="h-6 sm:h-8 w-6 sm:w-8" />
                  </div>
                </div>
              </motion.div>

              {/* Individual Customers Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-4 sm:p-6 text-white shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-purple-100 text-sm font-medium">Individual</p>
                    <p className="text-2xl sm:text-3xl font-bold">{stats.individualCustomers || 0}</p>
                  </div>
                  <div className="bg-purple-400/20 p-2 sm:p-3 rounded-xl">
                    <ChartBarIcon className="h-6 sm:h-8 w-6 sm:w-8" />
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Main Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20"
            >
              {/* Content */}
              <div className="px-4 sm:px-8 pb-4 sm:pb-8">
                <AnimatePresence mode="wait">
                  {viewMode === 'list' ? (
                    <motion.div
                      key="list"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <CustomerList
                        onCreateCustomer={handleCreateCustomer}
                        onEditCustomer={handleEditCustomer}
                        onViewCustomer={handleViewCustomer}
                        onDeleteCustomer={handleDeleteCustomer}
                        onBulkDelete={handleBulkDelete}
                        refreshTrigger={refreshTrigger}
                      />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="card"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <CardView
                        onCreateCustomer={handleCreateCustomer}
                        onEditCustomer={handleEditCustomer}
                        onViewCustomer={handleViewCustomer}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </motion.div>
        );
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-green-50 to-emerald-100 pt-4 sm:pt-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative overflow-hidden bg-gradient-to-r from-green-600 via-teal-600 to-emerald-700 shadow-2xl"
        >
          {/* Background Pattern */}
          <div className="absolute inset-0 bg-black/10">
            <div className="absolute inset-0" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }} />
          </div>

          <div className="relative px-4 py-6 sm:px-6 sm:py-4 lg:px-8 lg:py-10">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                <div>
                  <h1 className="text-3xl lg:text-4xl font-bold text-white mb-2">
                    {currentView === 'create' ? '✨ Create New Customer' : 
                     currentView === 'edit' ? '✏️ Edit Customer' : 
                     'Customer Management'}
                  </h1>
                  <p className="text-green-100 text-base lg:text-lg max-w-xl">
                    {currentView === 'create' ? 'Fill in the details to create a new customer' :
                     currentView === 'edit' ? 'Update customer information and manage details' :
                     'Build lasting relationships with your customers'}
                  </p>
                </div>
                <div className="flex items-center gap-4">
              

                  {currentView === 'list' && (
                    <motion.button
                      whileHover={{ scale: 1.05, boxShadow: "0 20px 40px -12px rgba(0, 0, 0, 0.25)" }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handleCreateCustomer}
                      className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-semibold rounded-xl text-green-600 bg-white hover:bg-gray-50 shadow-lg hover:shadow-xl transition-all duration-300 backdrop-blur-sm"
                    >
                      <PlusIcon className="h-5 w-5 mr-2" />
                      Add Customer
                    </motion.button>
                  )}
                </div>
              </div>
          </div>
        </motion.div>

        {/* Content */}
        <div className={`relative -mt-2 sm:-mt-4 pb-6 sm:pb-10 ${currentView === 'list' ? 'px-4 sm:px-6 lg:px-8' : ''}`}>
          <AnimatePresence mode="wait">
            {renderCurrentView()}
          </AnimatePresence>
        </div>
      </div>

      {/* Delete Confirmation Modals */}
      <AnimatePresence>
        {showDeleteModal && deletingCustomer && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm"
            onClick={() => setShowDeleteModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl shadow-2xl max-w-md w-full border border-gray-100"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
                  <TrashIcon className="w-8 h-8 text-red-600" />
                </div>

                <h3 className="text-xl font-bold text-gray-900 text-center mb-2">
                  Delete Customer
                </h3>

                <p className="text-gray-600 text-center mb-6">
                  Are you sure you want to delete <span className="font-semibold text-gray-900">"{deletingCustomer.firstName} {deletingCustomer.lastName}"</span>?
                  This action cannot be undone.
                </p>

                <div className="flex space-x-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setShowDeleteModal(false);
                      setDeletingCustomer(null);
                    }}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-all duration-200 font-semibold"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={confirmDeleteCustomer}
                    className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all duration-200 font-semibold shadow-lg"
                  >
                    Delete
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bulk Delete Confirmation Modal */}
      <AnimatePresence>
        {showBulkDeleteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm"
            onClick={() => setShowBulkDeleteModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl shadow-2xl max-w-md w-full border border-gray-100"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
                  <TrashIcon className="w-8 h-8 text-red-600" />
                </div>

                <h3 className="text-xl font-bold text-gray-900 text-center mb-2">
                  Delete Customers
                </h3>

                <p className="text-gray-600 text-center mb-6">
                  Are you sure you want to delete all selected customers?
                  This action cannot be undone.
                </p>

                <div className="flex space-x-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setShowBulkDeleteModal(false)}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-all duration-200 font-semibold"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      // This will be handled by the parent component
                      setShowBulkDeleteModal(false);
                    }}
                    className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all duration-200 font-semibold shadow-lg"
                  >
                    Delete All
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Layout>
  );
};

// Card View Component
const CardView = ({ onCreateCustomer, onEditCustomer, onViewCustomer }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  React.useEffect(() => {
    fetchCustomers();
  }, [statusFilter]);

  const fetchCustomers = async () => {
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const response = await api.get('/customers', { params });
      setCustomers(response.data.customers || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCustomer = async (customerId) => {
    if (window.confirm('Are you sure you want to delete this customer?')) {
      try {
        await api.delete(`/customers/${customerId}`);
        setCustomers(customers.filter(customer => customer._id !== customerId));
      } catch (error) {
        console.error('Error deleting customer:', error);
      }
    }
  };

  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = searchTerm === '' ||
      (customer.firstName + ' ' + customer.lastName).toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (customer.companyName && customer.companyName.toLowerCase().includes(searchTerm.toLowerCase()));

    return matchesSearch;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'inactive':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'suspended':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex items-center justify-center h-64"
      >
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="rounded-full h-12 w-12 border-b-2 border-green-600"
        />
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6"
    >
      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg border border-white/20 p-6"
      >
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label htmlFor="search" className="block text-sm font-semibold text-gray-700 mb-2">Search Customers</label>
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                id="search"
                placeholder="Search by name, email, or company..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-3 w-full border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white/50 backdrop-blur-sm transition-all duration-200 shadow-sm hover:shadow-md"
              />
            </div>
          </div>
          <div className="sm:w-48">
            <label htmlFor="status" className="block text-sm font-semibold text-gray-700 mb-2">Filter by Status</label>
            <div className="relative">
              <FunnelIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <select
                id="status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="pl-10 pr-4 py-3 w-full border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white/50 backdrop-blur-sm transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
          </div>
        </div>
        <div className="mt-4 text-sm text-gray-600 bg-gray-50/50 rounded-lg px-3 py-2 backdrop-blur-sm">
          Showing {filteredCustomers.length} of {customers.length} customers
        </div>
      </motion.div>

      {/* Cards */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
      >
        {filteredCustomers.map((customer, index) => (
          <motion.div
            key={customer._id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1, duration: 0.3 }}
            whileHover={{ y: -5, transition: { duration: 0.2 } }}
            className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300 border border-white/20 overflow-hidden group"
          >
            <div className="bg-gradient-to-r from-green-500 via-teal-500 to-emerald-600 p-6 relative overflow-hidden">
              {/* Background Pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0" style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M20 20c0-5.5-4.5-10-10-10s-10 4.5-10 10 4.5 10 10 10 10-4.5 10-10zm10 0c0-5.5-4.5-10-10-10s-10 4.5-10 10 4.5 10 10 10 10-4.5 10-10z'/%3E%3C/g%3E%3C/svg%3E")`,
                }} />
              </div>

              <div className="relative flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    className="h-14 w-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-lg border border-white/30"
                  >
                    <span className="text-xl font-bold text-white">
                      {customer.firstName?.charAt(0).toUpperCase() || '?'}
                      {customer.lastName?.charAt(0).toUpperCase() || ''}
                    </span>
                  </motion.div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-xl font-bold text-white truncate group-hover:text-green-100 transition-colors">
                      {customer.firstName} {customer.lastName}
                    </h3>
                    <p className="text-green-100 text-sm opacity-90">{customer.jobTitle || 'Customer'}</p>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <motion.button
                    whileHover={{ scale: 1.1, backgroundColor: 'rgba(255,255,255,0.2)' }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => onViewCustomer(customer._id)}
                    className="p-2 text-white/80 hover:text-white rounded-xl transition-all duration-200 backdrop-blur-sm"
                    title="View Details"
                  >
                    <EyeIcon className="h-5 w-5" />
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.1, backgroundColor: 'rgba(255,255,255,0.2)' }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => onEditCustomer(customer._id)}
                    className="p-2 text-white/80 hover:text-white rounded-xl transition-all duration-200 backdrop-blur-sm"
                    title="Edit Customer"
                  >
                    <PencilIcon className="h-5 w-5" />
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.1, backgroundColor: 'rgba(255,255,255,0.2)' }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => handleDeleteCustomer(customer._id)}
                    className="p-2 text-white/80 hover:text-white rounded-xl transition-all duration-200 backdrop-blur-sm"
                    title="Delete Customer"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </motion.button>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div className="space-y-3">
                <motion.div
                  whileHover={{ x: 5 }}
                  className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50/50 transition-colors"
                >
                  <EnvelopeIcon className="h-5 w-5 text-green-500" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-gray-900 truncate">{customer.email}</p>
                    <p className="text-xs text-gray-500">Email Address</p>
                  </div>
                </motion.div>

                {customer.companyName && (
                  <motion.div
                    whileHover={{ x: 5 }}
                    className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50/50 transition-colors"
                  >
                    <BuildingOfficeIcon className="h-5 w-5 text-blue-500" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-900 truncate">{customer.companyName}</p>
                      <p className="text-xs text-gray-500">Company</p>
                    </div>
                  </motion.div>
                )}

                {customer.phone && (
                  <motion.div
                    whileHover={{ x: 5 }}
                    className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50/50 transition-colors"
                  >
                    <PhoneIcon className="h-5 w-5 text-purple-500" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-900">{customer.phone}</p>
                      <p className="text-xs text-gray-500">Phone Number</p>
                    </div>
                  </motion.div>
                )}

                <motion.div
                  whileHover={{ x: 5 }}
                  className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50/50 transition-colors"
                >
                  <div className={`h-4 w-4 rounded-full border-2 border-white shadow-sm ${
                    customer.status === 'active' ? 'bg-green-500' :
                    customer.status === 'inactive' ? 'bg-gray-500' :
                    customer.status === 'suspended' ? 'bg-red-500' : 'bg-gray-500'
                  }`}></div>
                  <div className="min-w-0 flex-1">
                    <span className={`inline-flex px-3 py-1 text-xs font-bold rounded-full ${getStatusColor(customer.status || 'active')}`}>
                      {customer.status || 'Active'}
                    </span>
                    <p className="text-xs text-gray-500 mt-1">Account Status</p>
                  </div>
                </motion.div>

                <motion.div
                  whileHover={{ x: 5 }}
                  className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50/50 transition-colors"
                >
                  <CalendarDaysIcon className="h-5 w-5 text-indigo-500" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-gray-900">{new Date(customer.createdAt).toLocaleDateString()}</p>
                    <p className="text-xs text-gray-500">Member Since</p>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>
    </motion.div>
  );
};

export default CustomersPage;